import { useState, useEffect } from "react";
import { axiosInstance } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { UserCog, Key, Shield, Trash2, Box as BoxIcon, Crown } from "lucide-react";

export default function Users({ user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreateMasterDialog, setShowCreateMasterDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [masterPassword, setMasterPassword] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newMasterData, setNewMasterData] = useState({
    username: "",
    password: "",
    masterPassword: ""
  });
  
  const [permissions, setPermissions] = useState({
    can_register_sales: true,
    can_register_prizes: true,
    can_register_entries: true,
    can_register_exits: true,
    can_manage_customers: true,
    can_manage_debts: true,
    can_view_reports: true
  });

  // Permisos por caja (sólo Super Master)
  const [boxes, setBoxes] = useState([]);
  const [showBoxPermsDialog, setShowBoxPermsDialog] = useState(false);
  const [boxPermsMaster, setBoxPermsMaster] = useState(null);
  const [boxPermsSelection, setBoxPermsSelection] = useState([]);
  const [boxPermsPassword, setBoxPermsPassword] = useState("");

  const fetchBoxes = async () => {
    try {
      const r = await axiosInstance.get("/boxes");
      setBoxes(r.data || []);
    } catch (e) { /* ignore */ }
  };

  const openBoxPermsDialog = (masterUser) => {
    setBoxPermsMaster(masterUser);
    setBoxPermsSelection(masterUser.allowed_box_ids || []);
    setBoxPermsPassword("");
    setShowBoxPermsDialog(true);
  };

  const toggleBoxPerm = (boxId) => {
    setBoxPermsSelection((prev) =>
      prev.includes(boxId) ? prev.filter((id) => id !== boxId) : [...prev, boxId]
    );
  };

  const saveBoxPerms = async () => {
    if (!boxPermsMaster) return;
    if (!boxPermsPassword) { toast.error("Ingresa tu contraseña Master"); return; }
    try {
      await axiosInstance.put(`/users/${boxPermsMaster.id}/allowed-boxes`, {
        allowed_box_ids: boxPermsSelection.length === 0 ? null : boxPermsSelection,
        master_password: boxPermsPassword,
      });
      toast.success("Permisos de cajas actualizados");
      setShowBoxPermsDialog(false);
      fetchUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchBoxes();
  }, []);

  const openEditDialog = (cajeroUser) => {
    setSelectedUser(cajeroUser);
    setPermissions(cajeroUser.permissions || {
      can_register_sales: true,
      can_register_prizes: true,
      can_register_entries: true,
      can_register_exits: true,
      can_manage_customers: true,
      can_manage_debts: true,
      can_view_reports: true
    });
    setNewPassword("");
    setMasterPassword("");
    setShowEditDialog(true);
  };

  const handleUpdateUser = async () => {
    try {
      await axiosInstance.put('/users/manage', {
        user_id: selectedUser.id,
        master_password: masterPassword,
        new_password: newPassword || undefined,
        permissions: permissions
      });
      
      toast.success("Usuario actualizado exitosamente");
      setShowEditDialog(false);
      setSelectedUser(null);
      setMasterPassword("");
      setNewPassword("");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar usuario");
    }
  };

  const handleCreateMaster = async () => {
    if (!newMasterData.username || !newMasterData.password || !newMasterData.masterPassword) {
      toast.error("Completa todos los campos");
      return;
    }

    try {
      await axiosInstance.post(`/users/create-master?username=${encodeURIComponent(newMasterData.username)}&password=${encodeURIComponent(newMasterData.password)}&master_password=${encodeURIComponent(newMasterData.masterPassword)}`);
      
      toast.success(`Usuario Master '${newMasterData.username}' creado exitosamente`);
      setShowCreateMasterDialog(false);
      setNewMasterData({ username: "", password: "", masterPassword: "" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear usuario Master");
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser || !deletePassword) {
      toast.error("Ingrese la contraseña Master");
      return;
    }

    try {
      await axiosInstance.delete(`/users/${selectedUser.id}?password=${encodeURIComponent(deletePassword)}`);
      toast.success(`Usuario ${selectedUser.username} eliminado exitosamente`);
      setShowDeleteDialog(false);
      setSelectedUser(null);
      setDeletePassword("");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar usuario");
    }
  };

  const openDeleteDialog = (userToDelete) => {
    setSelectedUser(userToDelete);
    setDeletePassword("");
    setShowDeleteDialog(true);
  };

  if (loading) {
    return <div className="text-center py-20">Cargando...</div>;
  }

  if (user.role !== 'master' || !user.is_super_master) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-600">Sólo el Super Master puede gestionar usuarios</p>
      </div>
    );
  }

  const cajeros = users.filter(u => u.role === 'cajero');
  const masters = users.filter(u => u.role === 'master');

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Gestión de Usuarios
          </h1>
          <p className="text-slate-600">Administra cajeros, usuarios Master, contraseñas y permisos</p>
        </div>
        <Button
          onClick={() => setShowCreateMasterDialog(true)}
          className="bg-purple-600 hover:bg-purple-700 gap-2"
        >
          <Shield className="w-4 h-4" />
          Crear Usuario Master
        </Button>
      </div>

      {/* MASTER Users Section */}
      <div className="space-y-3">
        <h2 className="text-xl font-semibold text-purple-800 flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Usuarios Master ({masters.length})
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {masters.map((masterUser) => {
            const isSuper = (masterUser.email || "").toLowerCase() === "palistorecr2@gmail.com";
            const allowed = masterUser.allowed_box_ids;
            const allowedNames = (allowed || []).map(id => boxes.find(b => b.id === id)?.name || id);
            return (
            <Card
              key={masterUser.id}
              data-testid={`master-user-${masterUser.id}`}
              className="bg-purple-50 backdrop-blur-xl border-purple-200 shadow-lg hover:shadow-xl transition-shadow"
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-lg">
                  <div className="flex items-center gap-2">
                    {isSuper ? <Crown className="w-5 h-5 text-amber-500" /> : <Shield className="w-5 h-5 text-purple-600" />}
                    <div>
                      <p className="text-base">{masterUser.username}</p>
                      <p className="text-xs font-normal text-slate-500">{masterUser.email || 'Sin email'}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${isSuper ? "bg-amber-100 text-amber-800" : "bg-purple-100 text-purple-700"}`}>
                    {isSuper ? "Super Master" : "Master"}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {!isSuper && (
                  <div className="text-xs text-slate-700 space-y-1" data-testid={`master-allowed-boxes-${masterUser.id}`}>
                    <p className="font-semibold flex items-center gap-1">
                      <BoxIcon className="w-3.5 h-3.5" /> Cajas permitidas:
                    </p>
                    {!allowed || allowed.length === 0 ? (
                      <p className="italic text-slate-500">Todas las cajas (sin restricción)</p>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {allowedNames.map((n, i) => (
                          <span key={i} className="px-2 py-0.5 bg-white/70 border border-purple-200 rounded text-purple-800">{n}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  {!isSuper && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-purple-700 border-purple-300 hover:bg-purple-100"
                      onClick={() => openBoxPermsDialog(masterUser)}
                      data-testid={`master-edit-boxes-${masterUser.id}`}
                    >
                      <BoxIcon className="w-4 h-4 mr-1" />
                      Cajas permitidas
                    </Button>
                  )}
                  {masterUser.id !== user.id && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => openDeleteDialog(masterUser)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Eliminar
                    </Button>
                  )}
                  {masterUser.id === user.id && (
                    <span className="text-xs text-slate-500 italic">Tu cuenta</span>
                  )}
                </div>
              </CardContent>
            </Card>
          );})}
        </div>
      </div>

      {/* Cajeros Section */}
      <div className="space-y-3">
        <h2 className="text-xl font-semibold text-blue-800 flex items-center gap-2">
          <UserCog className="w-5 h-5" />
          Cajeros ({cajeros.length})
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cajeros.map((cajero) => (
            <Card
              key={cajero.id}
              data-testid={`user-${cajero.id}`}
              className="bg-white/80 backdrop-blur-xl border-slate-200 shadow-lg hover:shadow-xl transition-shadow"
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-lg">
                  <div className="flex items-center gap-2">
                    <UserCog className="w-5 h-5 text-blue-600" />
                    <div>
                    <p className="text-base">{cajero.username}</p>
                    <p className="text-xs font-normal text-slate-500">{cajero.assigned_box_name}</p>
                  </div>
                </div>
                <span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                  Cajero
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1">
                <p className="text-xs text-slate-600 font-semibold">Permisos:</p>
                <div className="grid grid-cols-2 gap-1">
                  {cajero.permissions?.can_register_sales && (
                    <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded">Ventas</span>
                  )}
                  {cajero.permissions?.can_register_prizes && (
                    <span className="text-xs bg-red-50 text-red-700 px-2 py-1 rounded">Premios</span>
                  )}
                  {cajero.permissions?.can_register_entries && (
                    <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">Entradas</span>
                  )}
                  {cajero.permissions?.can_register_exits && (
                    <span className="text-xs bg-orange-50 text-orange-700 px-2 py-1 rounded">Salidas</span>
                  )}
                  {cajero.permissions?.can_manage_customers && (
                    <span className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded">Clientes</span>
                  )}
                  {cajero.permissions?.can_manage_debts && (
                    <span className="text-xs bg-pink-50 text-pink-700 px-2 py-1 rounded">Deudas</span>
                  )}
                  {cajero.permissions?.can_view_reports && (
                    <span className="text-xs bg-slate-50 text-slate-700 px-2 py-1 rounded">Reportes</span>
                  )}
                </div>
              </div>

              <Button
                data-testid={`edit-user-${cajero.id}`}
                onClick={() => openEditDialog(cajero)}
                className="w-full bg-blue-600 hover:bg-blue-700 gap-2"
                size="sm"
              >
                <Shield className="w-4 h-4" />
                Gestionar
              </Button>

              <p className="text-xs text-slate-400 text-center">
                Creado: {new Date(cajero.created_at).toLocaleDateString('es-CR')}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

        {cajeros.length === 0 && (
          <Card className="bg-white/80 backdrop-blur-xl border-slate-200">
            <CardContent className="py-12">
              <p className="text-center text-slate-500">No hay cajeros registrados. Crea una caja para generar un cajero automáticamente.</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Edit User Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent data-testid="edit-user-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5" />
              Gestionar Usuario: {selectedUser?.username}
            </DialogTitle>
            <DialogDescription>
              Actualiza la contraseña y permisos del cajero
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Password Section */}
            <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Key className="w-4 h-4 text-slate-600" />
                <h3 className="font-semibold text-slate-800">Cambiar Contraseña</h3>
              </div>
              <div>
                <Label htmlFor="new_password">Nueva Contraseña para {selectedUser?.username}</Label>
                <Input
                  id="new_password"
                  data-testid="new-password-input"
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1.5"
                  placeholder="Dejar vacío para mantener la actual"
                />
              </div>
            </div>

            {/* Permissions Section */}
            <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="w-4 h-4 text-slate-600" />
                <h3 className="font-semibold text-slate-800">Permisos de Acceso</h3>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label htmlFor="perm_sales" className="flex-1">Registrar Ventas</Label>
                  <Switch
                    id="perm_sales"
                    data-testid="perm-sales-switch"
                    checked={permissions.can_register_sales}
                    onCheckedChange={(checked) => setPermissions({...permissions, can_register_sales: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="perm_prizes" className="flex-1">Registrar Premios</Label>
                  <Switch
                    id="perm_prizes"
                    data-testid="perm-prizes-switch"
                    checked={permissions.can_register_prizes}
                    onCheckedChange={(checked) => setPermissions({...permissions, can_register_prizes: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="perm_entries" className="flex-1">Registrar Entradas de Dinero</Label>
                  <Switch
                    id="perm_entries"
                    data-testid="perm-entries-switch"
                    checked={permissions.can_register_entries}
                    onCheckedChange={(checked) => setPermissions({...permissions, can_register_entries: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="perm_exits" className="flex-1">Registrar Salidas de Dinero</Label>
                  <Switch
                    id="perm_exits"
                    data-testid="perm-exits-switch"
                    checked={permissions.can_register_exits}
                    onCheckedChange={(checked) => setPermissions({...permissions, can_register_exits: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="perm_customers" className="flex-1">Gestionar Clientes</Label>
                  <Switch
                    id="perm_customers"
                    data-testid="perm-customers-switch"
                    checked={permissions.can_manage_customers}
                    onCheckedChange={(checked) => setPermissions({...permissions, can_manage_customers: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="perm_debts" className="flex-1">Gestionar Cuentas por Cobrar/Pagar</Label>
                  <Switch
                    id="perm_debts"
                    data-testid="perm-debts-switch"
                    checked={permissions.can_manage_debts}
                    onCheckedChange={(checked) => setPermissions({...permissions, can_manage_debts: checked})}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="perm_reports" className="flex-1">Ver Reportes</Label>
                  <Switch
                    id="perm_reports"
                    data-testid="perm-reports-switch"
                    checked={permissions.can_view_reports}
                    onCheckedChange={(checked) => setPermissions({...permissions, can_view_reports: checked})}
                  />
                </div>
              </div>
            </div>

            {/* Master Password Confirmation */}
            <div className="space-y-2">
              <Label htmlFor="master_password">Contraseña Master para Confirmar</Label>
              <Input
                id="master_password"
                data-testid="master-password-input"
                type="password"
                value={masterPassword}
                onChange={(e) => setMasterPassword(e.target.value)}
                className="mt-1.5"
                placeholder="Ingresa la contraseña master"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowEditDialog(false);
              setMasterPassword("");
              setNewPassword("");
            }}>
              Cancelar
            </Button>
            <Button
              data-testid="confirm-update-user-button"
              onClick={handleUpdateUser}
              className="bg-blue-600 hover:bg-blue-700"
              disabled={!masterPassword}
            >
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User/Master Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Eliminar Usuario
            </DialogTitle>
            <DialogDescription>
              ¿Está seguro de eliminar a <strong>{selectedUser?.username}</strong>? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                {selectedUser?.role === 'master' 
                  ? "⚠️ Está a punto de eliminar un usuario MASTER. Este usuario perderá acceso completo al sistema."
                  : "⚠️ Está a punto de eliminar un cajero. Este usuario ya no podrá acceder a su caja."}
              </p>
            </div>

            <div>
              <Label htmlFor="delete_password">Contraseña Master para Confirmar</Label>
              <Input
                id="delete_password"
                data-testid="delete-password-input"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Ingresa tu contraseña Master"
                className="mt-1.5"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteDialog(false);
                setSelectedUser(null);
                setDeletePassword("");
              }}
            >
              Cancelar
            </Button>
            <Button
              data-testid="confirm-delete-user-button"
              onClick={handleDeleteUser}
              className="bg-red-600 hover:bg-red-700"
              disabled={!deletePassword}
            >
              Eliminar Usuario
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Master Dialog */}
      <Dialog open={showCreateMasterDialog} onOpenChange={setShowCreateMasterDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              Crear Usuario Master
            </DialogTitle>
            <DialogDescription>
              Crea un nuevo usuario con permisos de Master (acceso completo)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="new_master_username">Nombre de Usuario</Label>
              <Input
                id="new_master_username"
                value={newMasterData.username}
                onChange={(e) => setNewMasterData({...newMasterData, username: e.target.value})}
                placeholder="Ej: ADMIN2"
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="new_master_password">Contraseña</Label>
              <Input
                id="new_master_password"
                type="password"
                value={newMasterData.password}
                onChange={(e) => setNewMasterData({...newMasterData, password: e.target.value})}
                placeholder="Contraseña para el nuevo Master"
                className="mt-1.5"
              />
            </div>

            <div className="pt-4 border-t">
              <Label htmlFor="confirm_master_password">Tu Contraseña Master (PALISTORE)</Label>
              <Input
                id="confirm_master_password"
                type="password"
                value={newMasterData.masterPassword}
                onChange={(e) => setNewMasterData({...newMasterData, masterPassword: e.target.value})}
                placeholder="Confirma tu contraseña"
                className="mt-1.5"
              />
              <p className="text-xs text-slate-500 mt-1">
                Ingresa tu contraseña para confirmar la creación
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCreateMasterDialog(false);
                setNewMasterData({ username: "", password: "", masterPassword: "" });
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateMaster}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Crear Master
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DIALOG: Permisos de Cajas por Master */}
      <Dialog open={showBoxPermsDialog} onOpenChange={setShowBoxPermsDialog}>
        <DialogContent data-testid="box-perms-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BoxIcon className="w-5 h-5" /> Cajas permitidas para {boxPermsMaster?.username}
            </DialogTitle>
            <DialogDescription>
              Selecciona las cajas que este Master podrá ver. Si no marcas ninguna, tendrá acceso a TODAS las cajas (sin restricción).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
            {boxes.length === 0 && (
              <p className="text-sm text-slate-500 text-center py-4">No hay cajas registradas.</p>
            )}
            {boxes.map((b) => {
              const checked = boxPermsSelection.includes(b.id);
              return (
                <label
                  key={b.id}
                  className={`flex items-center gap-2 p-2 border rounded cursor-pointer ${checked ? "bg-purple-50 border-purple-300" : "border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`box-perm-row-${b.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleBoxPerm(b.id)}
                    className="w-4 h-4"
                    data-testid={`box-perm-checkbox-${b.id}`}
                  />
                  <BoxIcon className="w-4 h-4 text-purple-600" />
                  <span className="font-medium">{b.name}</span>
                </label>
              );
            })}
          </div>
          <div className="pt-2 border-t">
            <Label className="text-sm">Tu contraseña Master (confirmación)</Label>
            <Input
              type="password"
              value={boxPermsPassword}
              onChange={(e) => setBoxPermsPassword(e.target.value)}
              placeholder="Contraseña"
              data-testid="box-perms-password"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBoxPermsDialog(false)}>Cancelar</Button>
            <Button onClick={saveBoxPerms} className="bg-purple-600 hover:bg-purple-700" data-testid="box-perms-save">
              Guardar permisos
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}